interface Feature {
	name: string;
	free: boolean | string;
	premium: boolean | string;
	pro: boolean | string;
	tooltip?: string;
	manualLink?: string;
}

const features: Feature[] = [
	// Features available in both Free and Premium (checked in Free)
	{
		name: "Create and manage events",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event RSVPs and notifications",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event editing and deletion",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event reminders",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#events",
	},
	{
		name: "Event threads",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#event_threads",
	},
	{
		name: "Event and poll channels",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#event_and_poll_channels",
	},
	{
		name: "Create and manage polls",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#polls",
	},
	{
		name: "Anonymous voting",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#polls",
	},
	{
		name: "Personal reminders",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#remind",
	},
	{
		name: "User preferences",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#user_preferences",
	},
	{
		name: "Server notifications",
		free: true,
		premium: true,
		pro: true,
		manualLink: "/manual#server_notifications",
	},

	// Features with differences between Free and Premium
	{
		name: "Event templates",
		free: "3 favorites per server",
		premium: "Unlimited favorites",
		pro: "Unlimited favorites",
		manualLink: "/manual#event_templates",
	},
	{
		name: "Server logs",
		free: "24 hours",
		premium: "Unlimited history",
		pro: "Unlimited history",
		manualLink: "/manual#server_logs",
	},
	{
		name: "Outbound calendar sync",
		free: "1 per user/server",
		premium: "5 per user/server",
		pro: "30 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "Google Calendar integration",
		free: "Basic",
		premium: "Full two-way sync",
		pro: "Full two-way sync",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "AI command usage",
		free: "Limited",
		premium: "Higher limits",
		pro: "Higher limits",
		manualLink: "/manual#ai",
	},

	// Premium-only features
	{
		name: "Recurring/repeating events",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#repeating_events",
	},
	{
		name: "Custom RSVP options with limits",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual/#custom_rsvp_options",
	},
	{
		name: "Attendee role automation",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#attendee_roles",
	},
	{
		name: "Role-based RSVP restrictions",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#role_restrictions",
	},
	{
		name: "Close RSVPs early",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#close_rsvps_early",
	},
	{
		name: "Role-based poll restrictions",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#role_restrictions",
	},
	{
		name: "Unlimited live list",
		free: false,
		premium: true,
		pro: true,
		manualLink: "/manual#live_list",
	},
	{
		name: "Inbound calendar sync",
		free: false,
		premium: "5 per user/server",
		pro: "30 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "Bidirectional calendar sync",
		free: false,
		premium: "5 per user/server",
		pro: "30 per user/server",
		manualLink: "/manual#google_calendar_sync",
	},
	{
		name: "Time Finder advanced settings",
		free: false,
		premium: true,
		pro: true,
	},

	// Pro-only features
	{
		name: "Bot name customization",
		free: false,
		premium: false,
		pro: true,
		manualLink: "/manual#bot_name",
	},
	{
		name: "Bot profile customization",
		free: false,
		premium: false,
		pro: true,
		manualLink: "/manual#bot_profile",
	},
	{
		name: "API access",
		free: false,
		premium: false,
		pro: true,
		manualLink: "/manual/api",
	},
	{
		name: "Webhooks",
		free: false,
		premium: false,
		pro: true,
		manualLink: "/manual/api#webhooks",
	},
	{
		name: "Business license",
		free: false,
		premium: false,
		pro: true,
	},
];

function FeatureValue({ value }: { value: boolean | string }) {
	if (typeof value === "boolean") {
		return value ? (
			<div className="flex justify-center">
				<i className="fas fa-check text-green-400"></i>
			</div>
		) : (
			<div className="flex justify-center">
				<i className="fas fa-times text-slate-500"></i>
			</div>
		);
	}

	return <div className="text-center text-xs text-slate-300 md:text-sm">{value}</div>;
}

export function FeatureComparisonTable() {
	return (
		<div className="py-16">
			<h2 className="mb-12 text-center text-4xl font-bold tracking-tight text-white">
				Feature Comparison
			</h2>

			<div className="-mx-4 overflow-x-auto px-4 sm:-mx-12 sm:px-12">
			<div className="mr-4 sm:mr-12">
			<div className="mx-auto min-w-[480px] max-w-5xl overflow-hidden rounded-xl border border-slate-700 bg-slate-800/30">
				{/* Header */}
				<div className="grid grid-cols-4 border-b border-slate-700 bg-slate-800/50">
					<div className="p-2 text-left md:p-4">
						<span className="text-sm font-semibold text-white md:text-lg">
							Features
						</span>
					</div>
					<div className="border-l border-slate-700 p-2 text-center md:p-4">
						<span className="text-sm font-semibold text-white md:text-lg">
							Free
						</span>
					</div>
					<div className="border-l border-slate-700 p-2 text-center md:p-4">
						<span className="text-sm font-semibold text-sesh-primary md:text-lg">
							Premium
						</span>
					</div>
					<div className="border-l border-slate-700 p-2 text-center md:p-4">
						<span className="bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-sm font-semibold text-transparent md:text-lg">
							Pro
						</span>
					</div>
				</div>

				{/* Feature rows */}
				{features.map((feature, index) => (
					<div
						key={index}
						className={`grid grid-cols-4 border-b border-slate-700/50 last:border-b-0 ${
							index % 2 === 0
								? "bg-slate-800/20"
								: "bg-slate-800/40"
						}`}
					>
						<div className="p-2 text-left md:p-4">
							{feature.manualLink ? (
								<a
									href={feature.manualLink}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center gap-1 text-xs text-slate-200 transition-colors duration-150 hover:text-slate-100 md:gap-2 md:text-base"
									title="Learn more"
								>
									<span>{feature.name}</span>
									<i className="fas fa-info-circle text-xs text-slate-400"></i>
								</a>
							) : (
								<span className="text-xs text-slate-200 md:text-base">
									{feature.name}
								</span>
							)}
						</div>
						<div className="border-l border-slate-700/50 p-2 md:p-4">
							<FeatureValue value={feature.free} />
						</div>
						<div className="border-l border-slate-700/50 p-2 md:p-4">
							<FeatureValue value={feature.premium} />
						</div>
						<div className="border-l border-slate-700/50 p-2 md:p-4">
							<FeatureValue value={feature.pro} />
						</div>
					</div>
				))}
			</div>
			</div>
			</div>

			<div className="mt-8 text-center">
				<p className="text-sm text-slate-400">
					Each tier includes everything from the previous tier, plus additional capabilities.
				</p>
			</div>
		</div>
	);
}
